<template>
  <!-- Show LoadingScreen after logging in -->
  <LoadingScreen 
    v-if="loadingScreen" 
    @loaded="loadingScreen = false" 
  />

  <!-- Show loading spinner while initializing -->
  <div v-if="loading" class="flex items-center justify-center h-screen">
    <LoadingSpinner />
  </div>

  <!-- Main content -->
  <div v-else class="min-h-screen flex flex-col">
    <HeaderComponent v-if="!isIframe || $userStore.isAdmin()" />
    <Toast class="sticky top-[50px] z-50 no-print" position="top-center" />
    <router-view class="flex-1"/>
    <FooterComponent v-if="!isIframe || $userStore.isAdmin()" class="no-print"/>
    
    <!-- Admin Floating Toolbar -->
    <FloatingAdminToolbar 
      v-if="hasAtLeastOneAdminTool()" 
      :tools="adminTools"
      main-button-tooltip="Admin Tools"
    />
    <PageEditor ref="pageEditor" @modal-closed="onPageEditorClosed" />
  </div>
</template>

<script>
import HeaderComponent from '@/Header/HeaderComponent.vue';
import FooterComponent from '@/Footer/FooterComponent.vue';
import LoadingSpinner from '@/components/Utils/LoadingSpinner.vue';
import LoadingScreen from '@/Header/LoadingScreen.vue';
import FloatingAdminToolbar from '@/components/Utils/FloatingAdminToolbar.vue';
import { LanguageIcon, BookOpenIcon, BugAntIcon } from '@heroicons/vue/24/outline';
import PageEditor from './Header/Utils/PageEditor.vue';
export default {
  components: {
    HeaderComponent,
    FooterComponent,
    LoadingSpinner,
    LoadingScreen,
    FloatingAdminToolbar,
    PageEditor
  },
  data() {
    return {
      loading: true, // Show a loading spinner until headers are loaded
      loadingScreen: false, // Control the loading screen visibility
      currentTime: Date.now(), // Add currentTime to data
      heartbeatInterval: null,
      error: null, // Store any error that might occur
      pageEditorOpen: false, // Track PageEditor state
    };
  },
  computed: {
    isIframe() {
      return this.$route.meta?.page?.iframe == "true";
    },
    startupError() {
      return this.$startupError || null;
    },
    adminTools() {
      return [
        {
          name: 'Translate Mode',
          icon: LanguageIcon,
          action: this.toggleTranslateMode,
          color: this.$debugStore.TranslateMode 
            ? 'bg-red-500 hover:bg-red-600 text-white' 
            : 'bg-blue-500 hover:bg-blue-600 text-white',
          tooltip: this.$debugStore.TranslateMode ? 'Disable Translate Mode' : 'Enable Translate Mode',
          permission: 'CanTranslate' // Users with translate permission can use this
        },
        {
          name: 'Page Editor',
          icon: BookOpenIcon,
          action: this.togglePageEditor,
          color: this.pageEditorOpen 
            ? 'bg-orange-500 hover:bg-orange-600 text-white' 
            : 'bg-purple-500 hover:bg-purple-600 text-white',
          tooltip: this.pageEditorOpen ? 'Close Page Editor' : 'Open Page Editor',
          permission: 'CanEditPages' // Users with edit pages permission can use this
        },
        {
          name: 'Debug Mode',
          icon: BugAntIcon,
          action: this.toggleDebugMode,
          color: 'bg-green-500 hover:bg-green-600 text-white',
          tooltip: 'Toggle Debug Mode'
        }
      ];
    }
  },
  async mounted() {
    this._timeInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 1000);
    try {
      // Load user session
      this.$userStore.loadUser();
      if (
        !this.$startupError &&
        !this.$userStore.user &&
        !this.$route.meta?.page?.iframe == "true" &&
        this.$route.path !== "/login"
      ) {
        this.$router.push('/login');
        this.loading = false;
        return;
      }

      // Fetch headers and ensure dynamic routes are loaded
      await this.$headerStore.fetchHeaders(
        this.$languageStore.selectedLanguage?.alternative ||
        this.$languageStore.selectedLanguage?.code ||
        'cs'
      );
    } catch (error) {
      this.error = "Failed to load application data. Please try again later.";
    } finally {
      this.loading = false; // Hide the loading spinner
    }
  },
  watch: {
    currentTime() {
      const timeSeconds = 5;
      const lastRequest = this.$debugStore.lastRequest;
      if (lastRequest && !this.$debugStore.heartbeatTriggered && document.visibilityState === 'visible' && this.$userStore.user) {
        // lastRequest is likely a string from toLocaleTimeString, so parse it to a Date
        const now = new Date();
        const [h, m, s] = lastRequest.split(':').map(Number);
        const last = new Date(now);
        last.setHours(h, m, s, 0);
        const diff = now - last;
        if (diff > timeSeconds * 1000) {
          this.$debugStore.triggerHeartbeat();
        }
      }
    },
    "$userStore.user"(newUser) {
      if (
        !newUser &&
        !this.$route.meta?.page?.iframe == "true" &&
        this.$route.path !== "/login"
      ) {
        this.$router.push('/login');
      }
    },
    "$userStore.justLoggedIn"(newValue) {
      if (newValue) {
        this.loadingScreen = true; // Show the LoadingScreen
      }
    },
  },
  methods: {
    hasAtLeastOneAdminTool() {
      return this.adminTools.some(tool => {
        if (tool.permission) {
          return this.$userStore.hasPermission(tool.permission);
        }
        return true; // No permission required
      });
    },
    toggleTranslateMode() {
      this.$debugStore.TranslateMode = !this.$debugStore.TranslateMode;
      
      // Show a toast notification
      if (this.$toast) {
        this.$toast.add({
          severity: this.$debugStore.TranslateMode ? "info" : "success",
          summary: "Debug Mode",
          detail: this.$debugStore.TranslateMode 
            ? "Translate Mode enabled - Click any text to edit translations" 
            : "Translate Mode disabled",
          life: 3000,
        });
      }
    },
    togglePageEditor() {
      this.pageEditorOpen = !this.pageEditorOpen;
      
      // Access the PageEditor component through ref and toggle its modal
      if (this.$refs.pageEditor) {
        this.$refs.pageEditor.showModal = this.pageEditorOpen;
      }
    },
    onPageEditorClosed() {
      this.pageEditorOpen = false;
    },
    toggleDebugMode(){
      this.$debugStore.showPageDebug = !this.$debugStore.showPageDebug;
    }
  }
};
</script>